import { Injectable } from '@angular/core';
import { HttpClient, HttpHeaders, HttpParams, HttpResponse } from '@angular/common/http';
import { Observable } from 'rxjs';
import { share } from 'rxjs/operators';
import _ from 'lodash';

import { BaseService } from '@Services/base.service';
import { GlobalMessages } from '@Services/global-messages';
import { LoggingService } from '@Services/logging.service';
import { LoadingService } from '@Services/loading.service';
import { ITypedServiceResponse } from '@Core/models/model';

import { DataContext } from '@Core/contexts/data-context';
import * as Models from '@Core/models/codegen/area.models';

class Response extends HttpResponse<any> { }

@Injectable()
export class PolicyService extends BaseService {

    public static baseUrl: string = "";

    constructor(protected http: HttpClient,
                protected globalMessages: GlobalMessages,
                protected loggingService: LoggingService,
                protected loadingService: LoadingService) {
        super(globalMessages, loggingService, loadingService);
        PolicyService.baseUrl = BaseService.baseUrl + "/Policy/";
    }

    public GetArea(contexts: DataContext[] | DataContext): Observable<ITypedServiceResponse<Models.Area>> {
        // Build the url
        var url = PolicyService.baseUrl + "Area";

        // Make the request
        let request = this.http.get<any>(url).pipe(share());

        // The user can pass in a single context. Convert it to a list.
        if (contexts && !Array.isArray(contexts))
            contexts = [contexts];

        // Update the contexts (if any provided)
        _.each(contexts, context => {
            this.handleNormalGetRequest(Models.Area, request, context);
        });
        return request;
    }

    public Lookupcitystatezipcities(): Observable<Response> {
        // Build the url
        var url = PolicyService.baseUrl + "/Lookup/CityStateZip/cities";

        // Make the request
        let request = this.http.get<any>(url).pipe(share());

        request.subscribe(() => {});

        return request;
    }

    public Lookupcitystatezipstates(): Observable<Response> {
        // Build the url
        var url = PolicyService.baseUrl + "/Lookup/CityStateZip/states";

        // Make the request
        let request = this.http.get<any>(url).pipe(share());

        request.subscribe(() => {});

        return request;
    }

    public Lookupcitystatezipzipcodes(): Observable<Response> {
        // Build the url
        var url = PolicyService.baseUrl + "/Lookup/CityStateZip/zipcodes";

        // Make the request
        let request = this.http.get<any>(url).pipe(share());

        request.subscribe(() => {});

        return request;
    }

    public Lookupcitystatezipcounties(): Observable<Response> {
        // Build the url
        var url = PolicyService.baseUrl + "/Lookup/CityStateZip/counties";

        // Make the request
        let request = this.http.get<any>(url).pipe(share());

        request.subscribe(() => {});

        return request;
    }

    public Lookuplegalentites(): Observable<Response> {
        // Build the url
        var url = PolicyService.baseUrl + "/Lookup/ReferenceData/LegalEntities";

        // Make the request
        let request = this.http.get<any>(url).pipe(share());

        request.subscribe(() => {});

        return request;
    }

    public Lookupratingcompanies(): Observable<Response> {
        // Build the url
        var url = PolicyService.baseUrl + "/Lookup/ReferenceData/RatingCompanies";

        // Make the request
        let request = this.http.get<any>(url).pipe(share());

        request.subscribe(() => {});

        return request;
    }

    public Lookuppolicystatuses(): Observable<Response> {
        // Build the url
        var url = PolicyService.baseUrl + "/Lookup/ReferenceData/PolicyStatuses";

        // Make the request
        let request = this.http.get<any>(url).pipe(share());

        request.subscribe(() => {});

        return request;
    }

    public Generateaccountnumber(body: any): Observable<Response> {
        // Build the url
        var url = PolicyService.baseUrl + "/Lookup/AccountNumber/actions/generate";

        // Make the request
        let request = this.http.post<any>(url, body).pipe(share());

        request.subscribe(() => {});

        return request;
    }

    public GetUnderwritingInfoForAScpPolicy(accountCode: string, transactionNumber: string, policyId: string): Observable<Response> {
        // Build the url
        var url = PolicyService.baseUrl + "MapToUW";

        // Add the query parameters that aren't null
        let params = new HttpParams();
        params = params.set('accountCode', accountCode);
        params = params.set('transactionNumber', transactionNumber);
        params = params.set('policyId', policyId);

        // Make the request
        let request = this.http.get<any>(url, { params }).pipe(share());

        request.subscribe(() => {});

        return request;
    }

    public Listcodesets(): Observable<Response> {
        // Build the url
        var url = PolicyService.baseUrl + "ProSuite/codeset";

        // Make the request
        let request = this.http.get<any>(url).pipe(share());

        request.subscribe(() => {});

        return request;
    }

    public Retrievecodeset(setCode: string): Observable<Response> {
        // Build the url
        var url = PolicyService.baseUrl + "ProSuite/codeset/{setCode}";
        url = url.replace("{setCode}", setCode);

        // Make the request
        let request = this.http.get<any>(url).pipe(share());

        request.subscribe(() => {});

        return request;
    }

    public Getglobaloptions(): Observable<Response> {
        // Build the url
        var url = PolicyService.baseUrl + "ProSuite/Config/GlobalOption";

        // Make the request
        let request = this.http.get<any>(url).pipe(share());

        request.subscribe(() => {});

        return request;
    }

    public Createglobaloption(body: any): Observable<Response> {
        // Build the url
        var url = PolicyService.baseUrl + "ProSuite/Config/GlobalOption";

        // Make the request
        let request = this.http.post<any>(url, body).pipe(share());

        request.subscribe(() => {});

        return request;
    }

    public Getintegrationactions(): Observable<Response> {
        // Build the url
        var url = PolicyService.baseUrl + "ProSuite/Config/Integration/action";

        // Make the request
        let request = this.http.get<any>(url).pipe(share());

        request.subscribe(() => {});

        return request;
    }

    public Getintegrationrequests(): Observable<Response> {
        // Build the url
        var url = PolicyService.baseUrl + "ProSuite/Config/Integration/request";

        // Make the request
        let request = this.http.get<any>(url).pipe(share());

        request.subscribe(() => {});

        return request;
    }

    public Getintegrationservices(): Observable<Response> {
        // Build the url
        var url = PolicyService.baseUrl + "ProSuite/Config/Integration/service";

        // Make the request
        let request = this.http.get<any>(url).pipe(share());

        request.subscribe(() => {});

        return request;
    }

    public Createintegrationservice(body: any): Observable<Response> {
        // Build the url
        var url = PolicyService.baseUrl + "ProSuite/Config/Integration/service";

        // Make the request
        let request = this.http.post<any>(url, body).pipe(share());

        request.subscribe(() => {});

        return request;
    }

    public Getintegrationservice(id: string): Observable<Response> {
        // Build the url
        var url = PolicyService.baseUrl + "ProSuite/Config/Integration/service/{id}";
        url = url.replace("{id}", id);

        // Make the request
        let request = this.http.get<any>(url).pipe(share());

        request.subscribe(() => {});

        return request;
    }

    public Getmenus(): Observable<Response> {
        // Build the url
        var url = PolicyService.baseUrl + "ProSuite/Config/Menu";

        // Make the request
        let request = this.http.get<any>(url).pipe(share());

        request.subscribe(() => {});

        return request;
    }

    public Createmenu(body: any): Observable<Response> {
        // Build the url
        var url = PolicyService.baseUrl + "ProSuite/Config/Menu";

        // Make the request
        let request = this.http.post<any>(url, body).pipe(share());

        request.subscribe(() => {});

        return request;
    }

    public Getmenu(id: string): Observable<Response> {
        // Build the url
        var url = PolicyService.baseUrl + "ProSuite/Config/Menu/{id}";
        url = url.replace("{id}", id);

        // Make the request
        let request = this.http.get<any>(url).pipe(share());

        request.subscribe(() => {});

        return request;
    }

    public Getconfigobjects(): Observable<Response> {
        // Build the url
        var url = PolicyService.baseUrl + "ProSuite/Config/object";

        // Make the request
        let request = this.http.get<any>(url).pipe(share());

        request.subscribe(() => {});

        return request;
    }

    public Getpagelayouts(): Observable<Response> {
        // Build the url
        var url = PolicyService.baseUrl + "ProSuite/Config/PageLayout";

        // Make the request
        let request = this.http.get<any>(url).pipe(share());

        request.subscribe(() => {});

        return request;
    }

    public Createpagelayout(body: any): Observable<Response> {
        // Build the url
        var url = PolicyService.baseUrl + "ProSuite/Config/PageLayout";

        // Make the request
        let request = this.http.post<any>(url, body).pipe(share());

        request.subscribe(() => {});

        return request;
    }

    public Getpagelayoutbyobjectcode(objectcode: string): Observable<Response> {
        // Build the url
        var url = PolicyService.baseUrl + "ProSuite/Config/PageLayout/{objectcode}";
        url = url.replace("{objectcode}", objectcode);

        // Make the request
        let request = this.http.get<any>(url).pipe(share());

        request.subscribe(() => {});

        return request;
    }

    public Getcomponentsettings(): Observable<Response> {
        // Build the url
        var url = PolicyService.baseUrl + "ProSuite/Config/PageLayout/componentsetting";

        // Make the request
        let request = this.http.get<any>(url).pipe(share());

        request.subscribe(() => {});

        return request;
    }

    public Createcomponentsetting(body: any): Observable<Response> {
        // Build the url
        var url = PolicyService.baseUrl + "ProSuite/Config/PageLayout/componentsetting";

        // Make the request
        let request = this.http.post<any>(url, body).pipe(share());

        request.subscribe(() => {});

        return request;
    }

    public Getcomponentsetting(id: string): Observable<Response> {
        // Build the url
        var url = PolicyService.baseUrl + "ProSuite/Config/PageLayout/componentsetting/{id}";
        url = url.replace("{id}", id);

        // Make the request
        let request = this.http.get<any>(url).pipe(share());

        request.subscribe(() => {});

        return request;
    }

    public Createpagelayoutorder(body: any): Observable<Response> {
        // Build the url
        var url = PolicyService.baseUrl + "ProSuite/Config/PageLayout/order";

        // Make the request
        let request = this.http.post<any>(url, body).pipe(share());

        request.subscribe(() => {});

        return request;
    }

    public Getpagelayoutorder(parentobjectcode: string): Observable<Response> {
        // Build the url
        var url = PolicyService.baseUrl + "ProSuite/Config/PageLayout/order/{parentobjectcode}";
        url = url.replace("{parentobjectcode}", parentobjectcode);

        // Make the request
        let request = this.http.get<any>(url).pipe(share());

        request.subscribe(() => {});

        return request;
    }

    public Getproductline(lineCode: string): Observable<Response> {
        // Build the url
        var url = PolicyService.baseUrl + "ProSuite/Config/productline/{lineCode}";
        url = url.replace("{lineCode}", lineCode);

        // Make the request
        let request = this.http.get<any>(url).pipe(share());

        request.subscribe(() => {});

        return request;
    }

    public Getstandardparagraphtemplates(): Observable<Response> {
        // Build the url
        var url = PolicyService.baseUrl + "ProSuite/Config/standardparagraph/template";

        // Make the request
        let request = this.http.get<any>(url).pipe(share());

        request.subscribe(() => {});

        return request;
    }

    public Getuserdefinedtable(objectcode: string): Observable<Response> {
        // Build the url
        var url = PolicyService.baseUrl + "ProSuite/Config/userdefinedtable/{objectcode}";
        url = url.replace("{objectcode}", objectcode);

        // Make the request
        let request = this.http.get<any>(url).pipe(share());

        request.subscribe(() => {});

        return request;
    }

    public Getconsumers(): Observable<Response> {
        // Build the url
        var url = PolicyService.baseUrl + "ProSuite/Consumer";

        // Make the request
        let request = this.http.get<any>(url).pipe(share());

        request.subscribe(() => {});

        return request;
    }

    public Createconsumer(body: any): Observable<Response> {
        // Build the url
        var url = PolicyService.baseUrl + "ProSuite/Consumer";

        // Make the request
        let request = this.http.post<any>(url, body).pipe(share());

        request.subscribe(() => {});

        return request;
    }

    public Getconsumer(id: string): Observable<Response> {
        // Build the url
        var url = PolicyService.baseUrl + "ProSuite/Consumer/{id}";
        url = url.replace("{id}", id);

        // Make the request
        let request = this.http.get<any>(url).pipe(share());

        request.subscribe(() => {});

        return request;
    }

    public Getconsumernotes(id: string): Observable<Response> {
        // Build the url
        var url = PolicyService.baseUrl + "ProSuite/Consumer/{id}/note";
        url = url.replace("{id}", id);

        // Make the request
        let request = this.http.get<any>(url).pipe(share());

        request.subscribe(() => {});

        return request;
    }

    public Createconsumernote(body: any): Observable<Response> {
        // Build the url
        var url = PolicyService.baseUrl + "ProSuite/Consumer/note";

        // Make the request
        let request = this.http.post<any>(url, body).pipe(share());

        request.subscribe(() => {});

        return request;
    }

    public Getconsumernote(id: string): Observable<Response> {
        // Build the url
        var url = PolicyService.baseUrl + "ProSuite/Consumer/note/{id}";
        url = url.replace("{id}", id);

        // Make the request
        let request = this.http.get<any>(url).pipe(share());

        request.subscribe(() => {});

        return request;
    }

    public Getconsumertemplate(): Observable<Response> {
        // Build the url
        var url = PolicyService.baseUrl + "ProSuite/Consumer/template";

        // Make the request
        let request = this.http.get<any>(url).pipe(share());

        request.subscribe(() => {});

        return request;
    }

    public Getcontracts(): Observable<Response> {
        // Build the url
        var url = PolicyService.baseUrl + "ProSuite/Contract";

        // Make the request
        let request = this.http.get<any>(url).pipe(share());

        request.subscribe(() => {});

        return request;
    }

    public Createcontract(body: any): Observable<Response> {
        // Build the url
        var url = PolicyService.baseUrl + "ProSuite/Contract";

        // Make the request
        let request = this.http.post<any>(url, body).pipe(share());

        request.subscribe(() => {});

        return request;
    }

    public Getcontractbyid(id: string): Observable<Response> {
        // Build the url
        var url = PolicyService.baseUrl + "ProSuite/Contract/{id}";
        url = url.replace("{id}", id);

        // Make the request
        let request = this.http.get<any>(url).pipe(share());

        request.subscribe(() => {});

        return request;
    }

    public Getcontractaffiliates(id: string): Observable<Response> {
        // Build the url
        var url = PolicyService.baseUrl + "ProSuite/Contract/{id}/affiliate";
        url = url.replace("{id}", id);

        // Make the request
        let request = this.http.get<any>(url).pipe(share());

        request.subscribe(() => {});

        return request;
    }

    public Getcontractcancelschedule(id: string): Observable<Response> {
        // Build the url
        var url = PolicyService.baseUrl + "ProSuite/Contract/{id}/cancel/schedule";
        url = url.replace("{id}", id);

        // Make the request
        let request = this.http.get<any>(url).pipe(share());

        request.subscribe(() => {});

        return request;
    }

    public Getcontractconditions(id: string): Observable<Response> {
        // Build the url
        var url = PolicyService.baseUrl + "ProSuite/Contract/{id}/condition";
        url = url.replace("{id}", id);

        // Make the request
        let request = this.http.get<any>(url).pipe(share());

        request.subscribe(() => {});

        return request;
    }

    public Getcontractdocuments(id: string): Observable<Response> {
        // Build the url
        var url = PolicyService.baseUrl + "ProSuite/Contract/{id}/document";
        url = url.replace("{id}", id);

        // Make the request
        let request = this.http.get<any>(url).pipe(share());

        request.subscribe(() => {});

        return request;
    }

    public Getcontractnotes(id: string): Observable<Response> {
        // Build the url
        var url = PolicyService.baseUrl + "ProSuite/Contract/{id}/note";
        url = url.replace("{id}", id);

        // Make the request
        let request = this.http.get<any>(url).pipe(share());

        request.subscribe(() => {});

        return request;
    }

    public Getcontractoptions(id: string): Observable<Response> {
        // Build the url
        var url = PolicyService.baseUrl + "ProSuite/Contract/{id}/option";
        url = url.replace("{id}", id);

        // Make the request
        let request = this.http.get<any>(url).pipe(share());

        request.subscribe(() => {});

        return request;
    }

    public Getcontractprojects(id: string): Observable<Response> {
        // Build the url
        var url = PolicyService.baseUrl + "ProSuite/Contract/{id}/project";
        url = url.replace("{id}", id);

        // Make the request
        let request = this.http.get<any>(url).pipe(share());

        request.subscribe(() => {});

        return request;
    }

    public Getcontractsetdocuments(id: string): Observable<Response> {
        // Build the url
        var url = PolicyService.baseUrl + "ProSuite/Contract/{id}/set/document";
        url = url.replace("{id}", id);

        // Make the request
        let request = this.http.get<any>(url).pipe(share());

        request.subscribe(() => {});

        return request;
    }

    public Getcontractsetnotes(id: string): Observable<Response> {
        // Build the url
        var url = PolicyService.baseUrl + "ProSuite/Contract/{id}/set/note";
        url = url.replace("{id}", id);

        // Make the request
        let request = this.http.get<any>(url).pipe(share());

        request.subscribe(() => {});

        return request;
    }

    public Getcontractsetprojects(id: string): Observable<Response> {
        // Build the url
        var url = PolicyService.baseUrl + "ProSuite/Contract/{id}/set/project";
        url = url.replace("{id}", id);

        // Make the request
        let request = this.http.get<any>(url).pipe(share());

        request.subscribe(() => {});

        return request;
    }

    public Getcontractsettasks(id: string): Observable<Response> {
        // Build the url
        var url = PolicyService.baseUrl + "ProSuite/Contract/{id}/set/task";
        url = url.replace("{id}", id);

        // Make the request
        let request = this.http.get<any>(url).pipe(share());

        request.subscribe(() => {});

        return request;
    }

    public Getcontractsubnotes(id: string): Observable<Response> {
        // Build the url
        var url = PolicyService.baseUrl + "ProSuite/Contract/{id}/sub/note";
        url = url.replace("{id}", id);

        // Make the request
        let request = this.http.get<any>(url).pipe(share());

        request.subscribe(() => {});

        return request;
    }

    public Getcontracttasks(id: string): Observable<Response> {
        // Build the url
        var url = PolicyService.baseUrl + "ProSuite/Contract/{id}/task";
        url = url.replace("{id}", id);

        // Make the request
        let request = this.http.get<any>(url).pipe(share());

        request.subscribe(() => {});

        return request;
    }

    public Createcontractaffiliate(body: any): Observable<Response> {
        // Build the url
        var url = PolicyService.baseUrl + "ProSuite/Contract/affiliate";

        // Make the request
        let request = this.http.post<any>(url, body).pipe(share());

        request.subscribe(() => {});

        return request;
    }

    public Createcontractapplication(body: any): Observable<Response> {
        // Build the url
        var url = PolicyService.baseUrl + "ProSuite/Contract/Application";

        // Make the request
        let request = this.http.post<any>(url, body).pipe(share());

        request.subscribe(() => {});

        return request;
    }

    public Getcontractapplication(id: string): Observable<Response> {
        // Build the url
        var url = PolicyService.baseUrl + "ProSuite/Contract/Application/{id}";
        url = url.replace("{id}", id);

        // Make the request
        let request = this.http.get<any>(url).pipe(share());

        request.subscribe(() => {});

        return request;
    }

    public Createcontractaudit(body: any): Observable<Response> {
        // Build the url
        var url = PolicyService.baseUrl + "ProSuite/Contract/Audit";

        // Make the request
        let request = this.http.post<any>(url, body).pipe(share());

        request.subscribe(() => {});

        return request;
    }

    public Getcontractaudit(id: string): Observable<Response> {
        // Build the url
        var url = PolicyService.baseUrl + "ProSuite/Contract/Audit/{id}";
        url = url.replace("{id}", id);

        // Make the request
        let request = this.http.get<any>(url).pipe(share());

        request.subscribe(() => {});

        return request;
    }

    public Getcontractaudittemplate(): Observable<Response> {
        // Build the url
        var url = PolicyService.baseUrl + "ProSuite/Contract/Audit/template";

        // Make the request
        let request = this.http.get<any>(url).pipe(share());

        request.subscribe(() => {});

        return request;
    }

    public Createcontractbind(body: any): Observable<Response> {
        // Build the url
        var url = PolicyService.baseUrl + "ProSuite/Contract/Bind";

        // Make the request
        let request = this.http.post<any>(url, body).pipe(share());

        request.subscribe(() => {});

        return request;
    }

    public Getcontractbind(id: string): Observable<Response> {
        // Build the url
        var url = PolicyService.baseUrl + "ProSuite/Contract/Bind/{id}";
        url = url.replace("{id}", id);

        // Make the request
        let request = this.http.get<any>(url).pipe(share());

        request.subscribe(() => {});

        return request;
    }

    public Getcontractbindtemplate(): Observable<Response> {
        // Build the url
        var url = PolicyService.baseUrl + "ProSuite/Contract/Bind/template";

        // Make the request
        let request = this.http.get<any>(url).pipe(share());

        request.subscribe(() => {});

        return request;
    }

    public Createcontractcancel(body: any): Observable<Response> {
        // Build the url
        var url = PolicyService.baseUrl + "ProSuite/Contract/Cancel";

        // Make the request
        let request = this.http.post<any>(url, body).pipe(share());

        request.subscribe(() => {});

        return request;
    }

    public Getcontractcancel(id: string): Observable<Response> {
        // Build the url
        var url = PolicyService.baseUrl + "ProSuite/Contract/Cancel/{id}";
        url = url.replace("{id}", id);

        // Make the request
        let request = this.http.get<any>(url).pipe(share());

        request.subscribe(() => {});

        return request;
    }

    public Createcontractcancelschedule(body: any): Observable<Response> {
        // Build the url
        var url = PolicyService.baseUrl + "ProSuite/Contract/Cancel/schedule";

        // Make the request
        let request = this.http.post<any>(url, body).pipe(share());

        request.subscribe(() => {});

        return request;
    }

    public Getcontractcancelschedulebyid(id: string): Observable<Response> {
        // Build the url
        var url = PolicyService.baseUrl + "ProSuite/Contract/Cancel/schedule/{id}";
        url = url.replace("{id}", id);

        // Make the request
        let request = this.http.get<any>(url).pipe(share());

        request.subscribe(() => {});

        return request;
    }

    public Getcontractcancelscheduletemplate(): Observable<Response> {
        // Build the url
        var url = PolicyService.baseUrl + "ProSuite/Contract/Cancel/schedule/template";

        // Make the request
        let request = this.http.get<any>(url).pipe(share());

        request.subscribe(() => {});

        return request;
    }

    public Getcontractcanceltemplate(): Observable<Response> {
        // Build the url
        var url = PolicyService.baseUrl + "ProSuite/Contract/Cancel/template";

        // Make the request
        let request = this.http.get<any>(url).pipe(share());

        request.subscribe(() => {});

        return request;
    }

    public Createcontractcertificate(body: any): Observable<Response> {
        // Build the url
        var url = PolicyService.baseUrl + "ProSuite/Contract/Certificate";

        // Make the request
        let request = this.http.post<any>(url, body).pipe(share());

        request.subscribe(() => {});

        return request;
    }

    public Getcontractcertificate(id: string): Observable<Response> {
        // Build the url
        var url = PolicyService.baseUrl + "ProSuite/Contract/Certificate/{id}";
        url = url.replace("{id}", id);

        // Make the request
        let request = this.http.get<any>(url).pipe(share());

        request.subscribe(() => {});

        return request;
    }

    public Getcontractcertificatedocument(id: string): Observable<Response> {
        // Build the url
        var url = PolicyService.baseUrl + "ProSuite/Contract/Certificate/{id}/document.pdf";
        url = url.replace("{id}", id);

        // Make the request
        let request = this.http.get<any>(url).pipe(share());

        request.subscribe(() => {});

        return request;
    }

    public Createcontractcertificatedocument(body: any): Observable<Response> {
        // Build the url
        var url = PolicyService.baseUrl + "ProSuite/Contract/Certificate/document";

        // Make the request
        let request = this.http.post<any>(url, body).pipe(share());

        request.subscribe(() => {});

        return request;
    }

    public Getcontractcertificatetemplate(): Observable<Response> {
        // Build the url
        var url = PolicyService.baseUrl + "ProSuite/Contract/Certificate/template";

        // Make the request
        let request = this.http.get<any>(url).pipe(share());

        request.subscribe(() => {});

        return request;
    }

    public Createcontractchangeconsumer(body: any): Observable<Response> {
        // Build the url
        var url = PolicyService.baseUrl + "ProSuite/Contract/ChangeConsumer";

        // Make the request
        let request = this.http.post<any>(url, body).pipe(share());

        request.subscribe(() => {});

        return request;
    }

    public Getcontractchangeconsumer(id: string): Observable<Response> {
        // Build the url
        var url = PolicyService.baseUrl + "ProSuite/Contract/ChangeConsumer/{id}";
        url = url.replace("{id}", id);

        // Make the request
        let request = this.http.get<any>(url).pipe(share());

        request.subscribe(() => {});

        return request;
    }

    public Getcontractchangeconsumertemplate(): Observable<Response> {
        // Build the url
        var url = PolicyService.baseUrl + "ProSuite/Contract/ChangeConsumer/template";

        // Make the request
        let request = this.http.get<any>(url).pipe(share());

        request.subscribe(() => {});

        return request;
    }

    public Createcontractclearance(body: any): Observable<Response> {
        // Build the url
        var url = PolicyService.baseUrl + "ProSuite/Contract/clearance";

        // Make the request
        let request = this.http.post<any>(url, body).pipe(share());

        request.subscribe(() => {});

        return request;
    }

    public Createcontractcondition(body: any): Observable<Response> {
        // Build the url
        var url = PolicyService.baseUrl + "ProSuite/Contract/condition";

        // Make the request
        let request = this.http.post<any>(url, body).pipe(share());

        request.subscribe(() => {});

        return request;
    }

    public Createcontractdisable(body: any): Observable<Response> {
        // Build the url
        var url = PolicyService.baseUrl + "ProSuite/Contract/Disable";

        // Make the request
        let request = this.http.post<any>(url, body).pipe(share());

        request.subscribe(() => {});

        return request;
    }

    public Getcontractdisable(id: string): Observable<Response> {
        // Build the url
        var url = PolicyService.baseUrl + "ProSuite/Contract/Disable/{id}";
        url = url.replace("{id}", id);

        // Make the request
        let request = this.http.get<any>(url).pipe(share());

        request.subscribe(() => {});

        return request;
    }

    public Getcontractdisabletemplate(): Observable<Response> {
        // Build the url
        var url = PolicyService.baseUrl + "ProSuite/Contract/Disable/template";

        // Make the request
        let request = this.http.get<any>(url).pipe(share());

        request.subscribe(() => {});

        return request;
    }

    public Createcontractdocument(body: any): Observable<Response> {
        // Build the url
        var url = PolicyService.baseUrl + "ProSuite/Contract/Document";

        // Make the request
        let request = this.http.post<any>(url, body).pipe(share());

        request.subscribe(() => {});

        return request;
    }

    public Getcontractdocumenttemplate(): Observable<Response> {
        // Build the url
        var url = PolicyService.baseUrl + "ProSuite/Contract/Document/template";

        // Make the request
        let request = this.http.get<any>(url).pipe(share());

        request.subscribe(() => {});

        return request;
    }

    public Createcontractendorsement(body: any): Observable<Response> {
        // Build the url
        var url = PolicyService.baseUrl + "ProSuite/Contract/Endorsement";

        // Make the request
        let request = this.http.post<any>(url, body).pipe(share());

        request.subscribe(() => {});

        return request;
    }

    public Getcontractendorsement(id: string): Observable<Response> {
        // Build the url
        var url = PolicyService.baseUrl + "ProSuite/Contract/Endorsement/{id}";
        url = url.replace("{id}", id);

        // Make the request
        let request = this.http.get<any>(url).pipe(share());

        request.subscribe(() => {});

        return request;
    }

    public Getcontractendorsementtemplate(): Observable<Response> {
        // Build the url
        var url = PolicyService.baseUrl + "ProSuite/Contract/Endorsement/template";

        // Make the request
        let request = this.http.get<any>(url).pipe(share());

        request.subscribe(() => {});

        return request;
    }

    public Createcontractextendterm(body: any): Observable<Response> {
        // Build the url
        var url = PolicyService.baseUrl + "ProSuite/Contract/ExtendTerm";

        // Make the request
        let request = this.http.post<any>(url, body).pipe(share());

        request.subscribe(() => {});

        return request;
    }

    public Getcontractextendterm(id: string): Observable<Response> {
        // Build the url
        var url = PolicyService.baseUrl + "ProSuite/Contract/ExtendTerm/{id}";
        url = url.replace("{id}", id);

        // Make the request
        let request = this.http.get<any>(url).pipe(share());

        request.subscribe(() => {});

        return request;
    }

    public Getcontractextendtermtemplate(): Observable<Response> {
        // Build the url
        var url = PolicyService.baseUrl + "ProSuite/Contract/ExtendTerm/template";

        // Make the request
        let request = this.http.get<any>(url).pipe(share());

        request.subscribe(() => {});

        return request;
    }

    public Createcontractfinance(body: any): Observable<Response> {
        // Build the url
        var url = PolicyService.baseUrl + "ProSuite/Contract/finance";

        // Make the request
        let request = this.http.post<any>(url, body).pipe(share());

        request.subscribe(() => {});

        return request;
    }

    public Getcontractinterbooktemplate(): Observable<Response> {
        // Build the url
        var url = PolicyService.baseUrl + "ProSuite/Contract/interbook/template";

        // Make the request
        let request = this.http.get<any>(url).pipe(share());

        request.subscribe(() => {});

        return request;
    }

    public Createcontractissue(body: any): Observable<Response> {
        // Build the url
        var url = PolicyService.baseUrl + "ProSuite/Contract/Issue";

        // Make the request
        let request = this.http.post<any>(url, body).pipe(share());

        request.subscribe(() => {});

        return request;
    }

    public Getcontractissue(id: string): Observable<Response> {
        // Build the url
        var url = PolicyService.baseUrl + "ProSuite/Contract/Issue/{id}";
        url = url.replace("{id}", id);

        // Make the request
        let request = this.http.get<any>(url).pipe(share());

        request.subscribe(() => {});

        return request;
    }

    public Getcontractissuetemplate(): Observable<Response> {
        // Build the url
        var url = PolicyService.baseUrl + "ProSuite/Contract/Issue/template";

        // Make the request
        let request = this.http.get<any>(url).pipe(share());

        request.subscribe(() => {});

        return request;
    }

    public Createcontractmarket(body: any): Observable<Response> {
        // Build the url
        var url = PolicyService.baseUrl + "ProSuite/Contract/Market";

        // Make the request
        let request = this.http.post<any>(url, body).pipe(share());

        request.subscribe(() => {});

        return request;
    }

    public Getcontractmarket(id: string): Observable<Response> {
        // Build the url
        var url = PolicyService.baseUrl + "ProSuite/Contract/Market/{id}";
        url = url.replace("{id}", id);

        // Make the request
        let request = this.http.get<any>(url).pipe(share());

        request.subscribe(() => {});

        return request;
    }

    public Getcontractmarkettemplate(): Observable<Response> {
        // Build the url
        var url = PolicyService.baseUrl + "ProSuite/Contract/Market/template";

        // Make the request
        let request = this.http.get<any>(url).pipe(share());

        request.subscribe(() => {});

        return request;
    }

    public Createcontractmarketset(body: any): Observable<Response> {
        // Build the url
        var url = PolicyService.baseUrl + "ProSuite/Contract/MarketSet";

        // Make the request
        let request = this.http.post<any>(url, body).pipe(share());

        request.subscribe(() => {});

        return request;
    }

    public Getcontractmarketset(id: string): Observable<Response> {
        // Build the url
        var url = PolicyService.baseUrl + "ProSuite/Contract/MarketSet/{id}";
        url = url.replace("{id}", id);

        // Make the request
        let request = this.http.get<any>(url).pipe(share());

        request.subscribe(() => {});

        return request;
    }

    public Createcontractmvr(body: any): Observable<Response> {
        // Build the url
        var url = PolicyService.baseUrl + "ProSuite/Contract/mvr";

        // Make the request
        let request = this.http.post<any>(url, body).pipe(share());

        request.subscribe(() => {});

        return request;
    }

    public Createcontractnonrenew(body: any): Observable<Response> {
        // Build the url
        var url = PolicyService.baseUrl + "ProSuite/Contract/NonRenew";

        // Make the request
        let request = this.http.post<any>(url, body).pipe(share());

        request.subscribe(() => {});

        return request;
    }

    public Getcontractnonrenew(id: string): Observable<Response> {
        // Build the url
        var url = PolicyService.baseUrl + "ProSuite/Contract/NonRenew/{id}";
        url = url.replace("{id}", id);

        // Make the request
        let request = this.http.get<any>(url).pipe(share());

        request.subscribe(() => {});

        return request;
    }

    public Getcontractnonrenewtemplate(): Observable<Response> {
        // Build the url
        var url = PolicyService.baseUrl + "ProSuite/Contract/NonRenew/template";

        // Make the request
        let request = this.http.get<any>(url).pipe(share());

        request.subscribe(() => {});

        return request;
    }

    public Createcontractnote(body: any): Observable<Response> {
        // Build the url
        var url = PolicyService.baseUrl + "ProSuite/Contract/note";

        // Make the request
        let request = this.http.post<any>(url, body).pipe(share());

        request.subscribe(() => {});

        return request;
    }

    public Getcontractnote(id: string): Observable<Response> {
        // Build the url
        var url = PolicyService.baseUrl + "ProSuite/Contract/note/{id}";
        url = url.replace("{id}", id);

        // Make the request
        let request = this.http.get<any>(url).pipe(share());

        request.subscribe(() => {});

        return request;
    }

    public Createcontractquote(body: any): Observable<Response> {
        // Build the url
        var url = PolicyService.baseUrl + "ProSuite/Contract/Quote";

        // Make the request
        let request = this.http.post<any>(url, body).pipe(share());

        request.subscribe(() => {});

        return request;
    }

    public Getcontractquote(id: string): Observable<Response> {
        // Build the url
        var url = PolicyService.baseUrl + "ProSuite/Contract/Quote/{id}";
        url = url.replace("{id}", id);

        // Make the request
        let request = this.http.get<any>(url).pipe(share());

        request.subscribe(() => {});

        return request;
    }

    public Getcontractquotetemplate(): Observable<Response> {
        // Build the url
        var url = PolicyService.baseUrl + "ProSuite/Contract/Quote/template";

        // Make the request
        let request = this.http.get<any>(url).pipe(share());

        request.subscribe(() => {});

        return request;
    }

    public Createcontractquoteset(body: any): Observable<Response> {
        // Build the url
        var url = PolicyService.baseUrl + "ProSuite/Contract/QuoteSet";

        // Make the request
        let request = this.http.post<any>(url, body).pipe(share());

        request.subscribe(() => {});

        return request;
    }

    public Getcontractquoteset(id: string): Observable<Response> {
        // Build the url
        var url = PolicyService.baseUrl + "ProSuite/Contract/QuoteSet/{id}";
        url = url.replace("{id}", id);

        // Make the request
        let request = this.http.get<any>(url).pipe(share());

        request.subscribe(() => {});

        return request;
    }

    public Createcontractrate(body: any): Observable<Response> {
        // Build the url
        var url = PolicyService.baseUrl + "ProSuite/Contract/Rate";

        // Make the request
        let request = this.http.post<any>(url, body).pipe(share());

        request.subscribe(() => {});

        return request;
    }

    public Getcontractrate(id: string): Observable<Response> {
        // Build the url
        var url = PolicyService.baseUrl + "ProSuite/Contract/Rate/{id}";
        url = url.replace("{id}", id);

        // Make the request
        let request = this.http.get<any>(url).pipe(share());

        request.subscribe(() => {});

        return request;
    }

    public Getcontractratetemplate(): Observable<Response> {
        // Build the url
        var url = PolicyService.baseUrl + "ProSuite/Contract/Rate/template";

        // Make the request
        let request = this.http.get<any>(url).pipe(share());

        request.subscribe(() => {});

        return request;
    }

    public Createcontractreinstate(body: any): Observable<Response> {
        // Build the url
        var url = PolicyService.baseUrl + "ProSuite/Contract/Reinstate";

        // Make the request
        let request = this.http.post<any>(url, body).pipe(share());

        request.subscribe(() => {});

        return request;
    }

    public Getcontractreinstate(id: string): Observable<Response> {
        // Build the url
        var url = PolicyService.baseUrl + "ProSuite/Contract/Reinstate/{id}";
        url = url.replace("{id}", id);

        // Make the request
        let request = this.http.get<any>(url).pipe(share());

        request.subscribe(() => {});

        return request;
    }

    public Getcontractreinstatetemplate(): Observable<Response> {
        // Build the url
        var url = PolicyService.baseUrl + "ProSuite/Contract/Reinstate/template";

        // Make the request
        let request = this.http.get<any>(url).pipe(share());

        request.subscribe(() => {});

        return request;
    }

    public Createcontractreissue(body: any): Observable<Response> {
        // Build the url
        var url = PolicyService.baseUrl + "ProSuite/Contract/Reissue";

        // Make the request
        let request = this.http.post<any>(url, body).pipe(share());

        request.subscribe(() => {});

        return request;
    }

    public Getcontractreissue(id: string): Observable<Response> {
        // Build the url
        var url = PolicyService.baseUrl + "ProSuite/Contract/Reissue/{id}";
        url = url.replace("{id}", id);

        // Make the request
        let request = this.http.get<any>(url).pipe(share());

        request.subscribe(() => {});

        return request;
    }

    public Getcontractreissuetemplate(): Observable<Response> {
        // Build the url
        var url = PolicyService.baseUrl + "ProSuite/Contract/Reissue/template";

        // Make the request
        let request = this.http.get<any>(url).pipe(share());

        request.subscribe(() => {});

        return request;
    }

    public Createcontractrenewal(body: any): Observable<Response> {
        // Build the url
        var url = PolicyService.baseUrl + "ProSuite/Contract/Renewal";

        // Make the request
        let request = this.http.post<any>(url, body).pipe(share());

        request.subscribe(() => {});

        return request;
    }

    public Getcontractrenewal(id: string): Observable<Response> {
        // Build the url
        var url = PolicyService.baseUrl + "ProSuite/Contract/Renewal/{id}";
        url = url.replace("{id}", id);

        // Make the request
        let request = this.http.get<any>(url).pipe(share());

        request.subscribe(() => {});

        return request;
    }

    public Getcontractrenewaltemplate(): Observable<Response> {
        // Build the url
        var url = PolicyService.baseUrl + "ProSuite/Contract/Renewal/template";

        // Make the request
        let request = this.http.get<any>(url).pipe(share());

        request.subscribe(() => {});

        return request;
    }

    public Createcontractrewrite(body: any): Observable<Response> {
        // Build the url
        var url = PolicyService.baseUrl + "ProSuite/Contract/Rewrite";

        // Make the request
        let request = this.http.post<any>(url, body).pipe(share());

        request.subscribe(() => {});

        return request;
    }

    public Getcontractrewrite(id: string): Observable<Response> {
        // Build the url
        var url = PolicyService.baseUrl + "ProSuite/Contract/Rewrite/{id}";
        url = url.replace("{id}", id);

        // Make the request
        let request = this.http.get<any>(url).pipe(share());

        request.subscribe(() => {});

        return request;
    }

    public Getcontractrewritetemplate(): Observable<Response> {
        // Build the url
        var url = PolicyService.baseUrl + "ProSuite/Contract/Rewrite/template";

        // Make the request
        let request = this.http.get<any>(url).pipe(share());

        request.subscribe(() => {});

        return request;
    }

    public Createcontractselfaudit(body: any): Observable<Response> {
        // Build the url
        var url = PolicyService.baseUrl + "ProSuite/Contract/SelfAudit";

        // Make the request
        let request = this.http.post<any>(url, body).pipe(share());

        request.subscribe(() => {});

        return request;
    }

    public Getcontractselfaudit(id: string): Observable<Response> {
        // Build the url
        var url = PolicyService.baseUrl + "ProSuite/Contract/SelfAudit/{id}";
        url = url.replace("{id}", id);

        // Make the request
        let request = this.http.get<any>(url).pipe(share());

        request.subscribe(() => {});

        return request;
    }

    public Getcontractselfaudittemplate(): Observable<Response> {
        // Build the url
        var url = PolicyService.baseUrl + "ProSuite/Contract/SelfAudit/template";

        // Make the request
        let request = this.http.get<any>(url).pipe(share());

        request.subscribe(() => {});

        return request;
    }

    public Getentities(): Observable<Response> {
        // Build the url
        var url = PolicyService.baseUrl + "ProSuite/Entity";

        // Make the request
        let request = this.http.get<any>(url).pipe(share());

        request.subscribe(() => {});

        return request;
    }

    public Createentity(body: any): Observable<Response> {
        // Build the url
        var url = PolicyService.baseUrl + "ProSuite/Entity";

        // Make the request
        let request = this.http.post<any>(url, body).pipe(share());

        request.subscribe(() => {});

        return request;
    }

    public Getentity(id: string): Observable<Response> {
        // Build the url
        var url = PolicyService.baseUrl + "ProSuite/Entity/{id}";
        url = url.replace("{id}", id);

        // Make the request
        let request = this.http.get<any>(url).pipe(share());

        request.subscribe(() => {});

        return request;
    }

    public Createentityaffiliate(body: any): Observable<Response> {
        // Build the url
        var url = PolicyService.baseUrl + "ProSuite/Entity/affiliate";

        // Make the request
        let request = this.http.post<any>(url, body).pipe(share());

        request.subscribe(() => {});

        return request;
    }

    public Getsuppliers(): Observable<Response> {
        // Build the url
        var url = PolicyService.baseUrl + "ProSuite/Supplier";

        // Make the request
        let request = this.http.get<any>(url).pipe(share());

        request.subscribe(() => {});

        return request;
    }

    public Createsupplier(body: any): Observable<Response> {
        // Build the url
        var url = PolicyService.baseUrl + "ProSuite/Supplier";

        // Make the request
        let request = this.http.post<any>(url, body).pipe(share());

        request.subscribe(() => {});

        return request;
    }

    public Getsupplier(id: string): Observable<Response> {
        // Build the url
        var url = PolicyService.baseUrl + "ProSuite/Supplier/{id}";
        url = url.replace("{id}", id);

        // Make the request
        let request = this.http.get<any>(url).pipe(share());

        request.subscribe(() => {});

        return request;
    }

    public Getsuppliertemplate(): Observable<Response> {
        // Build the url
        var url = PolicyService.baseUrl + "ProSuite/Supplier/template";

        // Make the request
        let request = this.http.get<any>(url).pipe(share());

        request.subscribe(() => {});

        return request;
    }

    public Getsupplierdocuments(id: string): Observable<Response> {
        // Build the url
        var url = PolicyService.baseUrl + "ProSuite/Supplier/{id}/document";
        url = url.replace("{id}", id);

        // Make the request
        let request = this.http.get<any>(url).pipe(share());

        request.subscribe(() => {});

        return request;
    }

    public Getsupplieragreementterms(): Observable<Response> {
        // Build the url
        var url = PolicyService.baseUrl + "ProSuite/Supplier/agreementterm";

        // Make the request
        let request = this.http.get<any>(url).pipe(share());

        request.subscribe(() => {});

        return request;
    }

    public Createsuppliernote(body: any): Observable<Response> {
        // Build the url
        var url = PolicyService.baseUrl + "ProSuite/Supplier/note";

        // Make the request
        let request = this.http.post<any>(url, body).pipe(share());

        request.subscribe(() => {});

        return request;
    }

    public Getsuppliernote(id: string): Observable<Response> {
        // Build the url
        var url = PolicyService.baseUrl + "ProSuite/Supplier/note/{id}";
        url = url.replace("{id}", id);

        // Make the request
        let request = this.http.get<any>(url).pipe(share());

        request.subscribe(() => {});

        return request;
    }

    public Getsuppliernotes(id: string): Observable<Response> {
        // Build the url
        var url = PolicyService.baseUrl + "ProSuite/Supplier/{id}/note";
        url = url.replace("{id}", id);

        // Make the request
        let request = this.http.get<any>(url).pipe(share());

        request.subscribe(() => {});

        return request;
    }

    public RetrievePageLayout(): Observable<Response> {
        // Build the url
        var url = PolicyService.baseUrl + "ProSuite/pagelayout";

        // Make the request
        let request = this.http.get<any>(url).pipe(share());

        request.subscribe(() => {});

        return request;
    }

    public ExecuteDbQuery(procName: string): Observable<Response> {
        // Build the url
        var url = PolicyService.baseUrl + "ProSuite/dbquery/{procName}";
        url = url.replace("{procName}", procName);

        // Make the request
        let request = this.http.get<any>(url).pipe(share());

        request.subscribe(() => {});

        return request;
    }

    public RetrieveMenu(): Observable<Response> {
        // Build the url
        var url = PolicyService.baseUrl + "ProSuite/menu";

        // Make the request
        let request = this.http.get<any>(url).pipe(share());

        request.subscribe(() => {});

        return request;
    }

    public GetLock(body: any): Observable<Response> {
        // Build the url
        var url = PolicyService.baseUrl + "ProSuite/lock";

        // Make the request
        let request = this.http.post<any>(url, body).pipe(share());

        request.subscribe(() => {});

        return request;
    }

    public GetSystemInfo(): Observable<Response> {
        // Build the url
        var url = PolicyService.baseUrl + "ProSuite/sysinfo";

        // Make the request
        let request = this.http.get<any>(url).pipe(share());

        request.subscribe(() => {});

        return request;
    }

    public CalledAssemblyResults(body: any): Observable<Response> {
        // Build the url
        var url = PolicyService.baseUrl + "/CalledAssemblyResults";

        // Make the request
        let request = this.http.post<any>(url, body).pipe(share());

        request.subscribe(() => {});

        return request;
    }

    public CancelProgress(body: any): Observable<Response> {
        // Build the url
        var url = PolicyService.baseUrl + "/CancelProgress";

        // Make the request
        let request = this.http.post<any>(url, body).pipe(share());

        request.subscribe(() => {});

        return request;
    }

    public ChangeFormSelections(body: any): Observable<Response> {
        // Build the url
        var url = PolicyService.baseUrl + "/ChangeFormSelections";

        // Make the request
        let request = this.http.post<any>(url, body).pipe(share());

        request.subscribe(() => {});

        return request;
    }

    public Close(body: any): Observable<Response> {
        // Build the url
        var url = PolicyService.baseUrl + "/Close";

        // Make the request
        let request = this.http.post<any>(url, body).pipe(share());

        request.subscribe(() => {});

        return request;
    }

    public Commit(body: any): Observable<Response> {
        // Build the url
        var url = PolicyService.baseUrl + "/Commit";

        // Make the request
        let request = this.http.post<any>(url, body).pipe(share());

        request.subscribe(() => {});

        return request;
    }

    public CopyInstance(body: any): Observable<Response> {
        // Build the url
        var url = PolicyService.baseUrl + "/CopyInstance";

        // Make the request
        let request = this.http.post<any>(url, body).pipe(share());

        request.subscribe(() => {});

        return request;
    }

    public DeleteInstance(body: any): Observable<Response> {
        // Build the url
        var url = PolicyService.baseUrl + "/DeleteInstance";

        // Make the request
        let request = this.http.post<any>(url, body).pipe(share());

        request.subscribe(() => {});

        return request;
    }

    public DeleteOverrides(body: any): Observable<Response> {
        // Build the url
        var url = PolicyService.baseUrl + "/DeleteOverrides";

        // Make the request
        let request = this.http.post<any>(url, body).pipe(share());

        request.subscribe(() => {});

        return request;
    }

    public DialogPostProcessing(body: any): Observable<Response> {
        // Build the url
        var url = PolicyService.baseUrl + "/DialogPostProcessing";

        // Make the request
        let request = this.http.post<any>(url, body).pipe(share());

        request.subscribe(() => {});

        return request;
    }

    public DisableRevisionPosting(body: any): Observable<Response> {
        // Build the url
        var url = PolicyService.baseUrl + "/DisableRevisionPosting";

        // Make the request
        let request = this.http.post<any>(url, body).pipe(share());

        request.subscribe(() => {});

        return request;
    }

    public EnableDebug(body: any): Observable<Response> {
        // Build the url
        var url = PolicyService.baseUrl + "/EnableDebug";

        // Make the request
        let request = this.http.post<any>(url, body).pipe(share());

        request.subscribe(() => {});

        return request;
    }

    public EnableOverrideMode(body: any): Observable<Response> {
        // Build the url
        var url = PolicyService.baseUrl + "/EnableOverrideMode";

        // Make the request
        let request = this.http.post<any>(url, body).pipe(share());

        request.subscribe(() => {});

        return request;
    }

    public EnableRevisionPosting(body: any): Observable<Response> {
        // Build the url
        var url = PolicyService.baseUrl + "/EnableRevisionPosting";

        // Make the request
        let request = this.http.post<any>(url, body).pipe(share());

        request.subscribe(() => {});

        return request;
    }

    public GenerateGhostDraftDebugData(body: any): Observable<Response> {
        // Build the url
        var url = PolicyService.baseUrl + "/GenerateGhostDraftDebugData";

        // Make the request
        let request = this.http.post<any>(url, body).pipe(share());

        request.subscribe(() => {});

        return request;
    }

    public GenerateGhostDraftScenario(body: any): Observable<Response> {
        // Build the url
        var url = PolicyService.baseUrl + "/GenerateGhostDraftScenario";

        // Make the request
        let request = this.http.post<any>(url, body).pipe(share());

        request.subscribe(() => {});

        return request;
    }

    public GetEquations(body: any): Observable<Response> {
        // Build the url
        var url = PolicyService.baseUrl + "/GetEquations";

        // Make the request
        let request = this.http.post<any>(url, body).pipe(share());

        request.subscribe(() => {});

        return request;
    }

    public GetFillerForm(body: any): Observable<Response> {
        // Build the url
        var url = PolicyService.baseUrl + "/GetFillerForm";

        // Make the request
        let request = this.http.post<any>(url, body).pipe(share());

        request.subscribe(() => {});

        return request;
    }

    public GetFootnotes(body: any): Observable<Response> {
        // Build the url
        var url = PolicyService.baseUrl + "/GetFootnotes";

        // Make the request
        let request = this.http.post<any>(url, body).pipe(share());

        request.subscribe(() => {});

        return request;
    }

    public GetFormEditionList(body: any): Observable<Response> {
        // Build the url
        var url = PolicyService.baseUrl + "/GetFormEditionList";

        // Make the request
        let request = this.http.post<any>(url, body).pipe(share());

        request.subscribe(() => {});

        return request;
    }

    public GetFormPrintList(body: any): Observable<Response> {
        // Build the url
        var url = PolicyService.baseUrl + "/GetFormPrintList";

        // Make the request
        let request = this.http.post<any>(url, body).pipe(share());

        request.subscribe(() => {});

        return request;
    }

    public GetFormSelectFilterList(body: any): Observable<Response> {
        // Build the url
        var url = PolicyService.baseUrl + "/GetFormSelectFilterList";

        // Make the request
        let request = this.http.post<any>(url, body).pipe(share());

        request.subscribe(() => {});

        return request;
    }

    public GetFormSelectList(body: any): Observable<Response> {
        // Build the url
        var url = PolicyService.baseUrl + "/GetFormSelectList";

        // Make the request
        let request = this.http.post<any>(url, body).pipe(share());

        request.subscribe(() => {});

        return request;
    }

    public GetFormsOnPolicyList(body: any): Observable<Response> {
        // Build the url
        var url = PolicyService.baseUrl + "/GetFormsOnPolicyList";

        // Make the request
        let request = this.http.post<any>(url, body).pipe(share());

        request.subscribe(() => {});

        return request;
    }

    public GetFormStateList(body: any): Observable<Response> {
        // Build the url
        var url = PolicyService.baseUrl + "/GetFormStateList";

        // Make the request
        let request = this.http.post<any>(url, body).pipe(share());

        request.subscribe(() => {});

        return request;
    }

    public GetListContent(body: any): Observable<Response> {
        // Build the url
        var url = PolicyService.baseUrl + "/GetListContent";

        // Make the request
        let request = this.http.post<any>(url, body).pipe(share());

        request.subscribe(() => {});

        return request;
    }

    public GetScreen(body: any): Observable<Response> {
        // Build the url
        var url = PolicyService.baseUrl + "/GetScreen";

        // Make the request
        let request = this.http.post<any>(url, body).pipe(share());

        request.subscribe(() => {});

        return request;
    }

    public GetWorksheetPrintList(body: any): Observable<Response> {
        // Build the url
        var url = PolicyService.baseUrl + "/GetWorksheetPrintList";

        // Make the request
        let request = this.http.post<any>(url, body).pipe(share());

        request.subscribe(() => {});

        return request;
    }

    public NavigateTo(body: any): Observable<Response> {
        // Build the url
        var url = PolicyService.baseUrl + "/NavigateTo";

        // Make the request
        let request = this.http.post<any>(url, body).pipe(share());

        request.subscribe(() => {});

        return request;
    }

    public NewQuote(body: any): Observable<Response> {
        // Build the url
        var url = PolicyService.baseUrl + "/NewQuote";

        // Make the request
        let request = this.http.post<any>(url, body).pipe(share());

        request.subscribe(() => {});

        return request;
    }

    public NewTransaction(body: any): Observable<Response> {
        // Build the url
        var url = PolicyService.baseUrl + "/NewTransaction";

        // Make the request
        let request = this.http.post<any>(url, body).pipe(share());

        request.subscribe(() => {});

        return request;
    }

    public Open(body: any): Observable<Response> {
        // Build the url
        var url = PolicyService.baseUrl + "/Open";

        // Make the request
        let request = this.http.post<any>(url, body).pipe(share());

        request.subscribe(() => {});

        return request;
    }

    public OpenTempSave(body: any): Observable<Response> {
        // Build the url
        var url = PolicyService.baseUrl + "/OpenTempSave";

        // Make the request
        let request = this.http.post<any>(url, body).pipe(share());

        request.subscribe(() => {});

        return request;
    }

    public PasteInstance(body: any): Observable<Response> {
        // Build the url
        var url = PolicyService.baseUrl + "/PasteInstance";

        // Make the request
        let request = this.http.post<any>(url, body).pipe(share());

        request.subscribe(() => {});

        return request;
    }

    public PasteSpecialInstance(body: any): Observable<Response> {
        // Build the url
        var url = PolicyService.baseUrl + "/PasteSpecialInstance";

        // Make the request
        let request = this.http.post<any>(url, body).pipe(share());

        request.subscribe(() => {});

        return request;
    }

    public PrintFormList(body: any): Observable<Response> {
        // Build the url
        var url = PolicyService.baseUrl + "/PrintFormList";

        // Make the request
        let request = this.http.post<any>(url, body).pipe(share());

        request.subscribe(() => {});

        return request;
    }

    public PrintToHistory(body: any): Observable<Response> {
        // Build the url
        var url = PolicyService.baseUrl + "/PrintToHistory";

        // Make the request
        let request = this.http.post<any>(url, body).pipe(share());

        request.subscribe(() => {});

        return request;
    }

    public PrintWorksheetList(body: any): Observable<Response> {
        // Build the url
        var url = PolicyService.baseUrl + "/PrintWorksheetList";

        // Make the request
        let request = this.http.post<any>(url, body).pipe(share());

        request.subscribe(() => {});

        return request;
    }

    public Rate(body: any): Observable<Response> {
        // Build the url
        var url = PolicyService.baseUrl + "/Rate";

        // Make the request
        let request = this.http.post<any>(url, body).pipe(share());

        request.subscribe(() => {});

        return request;
    }

    public ReinstateInstance(body: any): Observable<Response> {
        // Build the url
        var url = PolicyService.baseUrl + "/ReinstateInstance";

        // Make the request
        let request = this.http.post<any>(url, body).pipe(share());

        request.subscribe(() => {});

        return request;
    }

    public ReOpen(body: any): Observable<Response> {
        // Build the url
        var url = PolicyService.baseUrl + "/ReOpen";

        // Make the request
        let request = this.http.post<any>(url, body).pipe(share());

        request.subscribe(() => {});

        return request;
    }

    public ResultLinkClicked(body: any): Observable<Response> {
        // Build the url
        var url = PolicyService.baseUrl + "/ResultLinkClicked";

        // Make the request
        let request = this.http.post<any>(url, body).pipe(share());

        request.subscribe(() => {});

        return request;
    }

    public Revert(body: any): Observable<Response> {
        // Build the url
        var url = PolicyService.baseUrl + "/Revert";

        // Make the request
        let request = this.http.post<any>(url, body).pipe(share());

        request.subscribe(() => {});

        return request;
    }

    public Save(body: any): Observable<Response> {
        // Build the url
        var url = PolicyService.baseUrl + "/Save";

        // Make the request
        let request = this.http.post<any>(url, body).pipe(share());

        request.subscribe(() => {});

        return request;
    }

    public SaveCanceled(body: any): Observable<Response> {
        // Build the url
        var url = PolicyService.baseUrl + "/SaveCanceled";

        // Make the request
        let request = this.http.post<any>(url, body).pipe(share());

        request.subscribe(() => {});

        return request;
    }

    public SaveFillableData(body: any): Observable<Response> {
        // Build the url
        var url = PolicyService.baseUrl + "/SaveFillableData";

        // Make the request
        let request = this.http.post<any>(url, body).pipe(share());

        request.subscribe(() => {});

        return request;
    }

    public SetEquations(body: any): Observable<Response> {
        // Build the url
        var url = PolicyService.baseUrl + "/SetEquations";

        // Make the request
        let request = this.http.post<any>(url, body).pipe(share());

        request.subscribe(() => {});

        return request;
    }

    public SetField(body: any): Observable<Response> {
        // Build the url
        var url = PolicyService.baseUrl + "/SetField";

        // Make the request
        let request = this.http.post<any>(url, body).pipe(share());

        request.subscribe(() => {});

        return request;
    }

    public SetQuoteStatus(body: any): Observable<Response> {
        // Build the url
        var url = PolicyService.baseUrl + "/SetQuoteStatus";

        // Make the request
        let request = this.http.post<any>(url, body).pipe(share());

        request.subscribe(() => {});

        return request;
    }

    public SplitInstance(body: any): Observable<Response> {
        // Build the url
        var url = PolicyService.baseUrl + "/SplitInstance";

        // Make the request
        let request = this.http.post<any>(url, body).pipe(share());

        request.subscribe(() => {});

        return request;
    }

    public StartTracing(body: any): Observable<Response> {
        // Build the url
        var url = PolicyService.baseUrl + "/StartTracing";

        // Make the request
        let request = this.http.post<any>(url, body).pipe(share());

        request.subscribe(() => {});

        return request;
    }

    public StopTracing(body: any): Observable<Response> {
        // Build the url
        var url = PolicyService.baseUrl + "/StopTracing";

        // Make the request
        let request = this.http.post<any>(url, body).pipe(share());

        request.subscribe(() => {});

        return request;
    }

    public UpdateNumber(body: any): Observable<Response> {
        // Build the url
        var url = PolicyService.baseUrl + "/UpdateNumber";

        // Make the request
        let request = this.http.post<any>(url, body).pipe(share());

        request.subscribe(() => {});

        return request;
    }

    public BeginWaitForResults(body: any): Observable<Response> {
        // Build the url
        var url = PolicyService.baseUrl + "/BeginWaitForResults";

        // Make the request
        let request = this.http.post<any>(url, body).pipe(share());

        request.subscribe(() => {});

        return request;
    }

    public EndWaitForResults(body: any): Observable<Response> {
        // Build the url
        var url = PolicyService.baseUrl + "/EndWaitForResults";

        // Make the request
        let request = this.http.post<any>(url, body).pipe(share());

        request.subscribe(() => {});

        return request;
    }

    public ExitResults(body: any): Observable<Response> {
        // Build the url
        var url = PolicyService.baseUrl + "/ExitResults";

        // Make the request
        let request = this.http.post<any>(url, body).pipe(share());

        request.subscribe(() => {});

        return request;
    }

    public InitializeResults(body: any): Observable<Response> {
        // Build the url
        var url = PolicyService.baseUrl + "/InitializeResults";

        // Make the request
        let request = this.http.post<any>(url, body).pipe(share());

        request.subscribe(() => {});

        return request;
    }

    public StopWaitingForResults(body: any): Observable<Response> {
        // Build the url
        var url = PolicyService.baseUrl + "/StopWaitingForResults";

        // Make the request
        let request = this.http.post<any>(url, body).pipe(share());

        request.subscribe(() => {});

        return request;
    }

    public WaitForResults(body: any): Observable<Response> {
        // Build the url
        var url = PolicyService.baseUrl + "/WaitForResults";

        // Make the request
        let request = this.http.post<any>(url, body).pipe(share());

        request.subscribe(() => {});

        return request;
    }

    public CreateProducer(body: any): Observable<Response> {
        // Build the url
        var url = PolicyService.baseUrl + "/Producers";

        // Make the request
        let request = this.http.post<any>(url, body).pipe(share());

        request.subscribe(() => {});

        return request;
    }

    public ListProducers(): Observable<Response> {
        // Build the url
        var url = PolicyService.baseUrl + "/Producers";

        // Make the request
        let request = this.http.get<any>(url).pipe(share());

        request.subscribe(() => {});

        return request;
    }

    public UpdateProducer(body: any, id: string): Observable<Response> {
        // Build the url
        var url = PolicyService.baseUrl + "/Producers/{id}";
        url = url.replace("{id}", id);

        // Make the request
        let request = this.http.patch<any>(url, body).pipe(share());

        request.subscribe(() => {});

        return request;
    }

    public GetProducer(codeOrId: string): Observable<Response> {
        // Build the url
        var url = PolicyService.baseUrl + "/Producers/{codeOrId}";
        url = url.replace("{codeOrId}", codeOrId);

        // Make the request
        let request = this.http.get<any>(url).pipe(share());

        request.subscribe(() => {});

        return request;
    }

    public Getreqaccountfields(numberingTaskType: string): Observable<Response> {
        // Build the url
        var url = PolicyService.baseUrl + "AccountNumber/required-account-fields/{numberingTaskType}";
        url = url.replace("{numberingTaskType}", numberingTaskType);

        // Make the request
        let request = this.http.get<any>(url).pipe(share());

        request.subscribe(() => {});

        return request;
    }

    public Accountnumbergenerate(body: any): Observable<Response> {
        // Build the url
        var url = PolicyService.baseUrl + "AccountNumber/actions/generate";

        // Make the request
        let request = this.http.post<any>(url, body).pipe(share());

        request.subscribe(() => {});

        return request;
    }

    public Accountnumbercommit(body: any): Observable<Response> {
        // Build the url
        var url = PolicyService.baseUrl + "AccountNumber/actions/commit";

        // Make the request
        let request = this.http.post<any>(url, body).pipe(share());

        request.subscribe(() => {});

        return request;
    }

    public GetAccounts(): Observable<Response> {
        // Build the url
        var url = PolicyService.baseUrl + "/Accounts";

        // Make the request
        let request = this.http.get<any>(url).pipe(share());

        request.subscribe(() => {});

        return request;
    }

    public CreateAccount(body: any): Observable<Response> {
        // Build the url
        var url = PolicyService.baseUrl + "/Accounts";

        // Make the request
        let request = this.http.post<any>(url, body).pipe(share());

        request.subscribe(() => {});

        return request;
    }

    public GetAccount(id: string): Observable<Response> {
        // Build the url
        var url = PolicyService.baseUrl + "/Accounts/{id}";
        url = url.replace("{id}", id);

        // Make the request
        let request = this.http.get<any>(url).pipe(share());

        request.subscribe(() => {});

        return request;
    }

    public DeleteAccount(idOfDeletedItem: string, contexts: DataContext[] | DataContext, id: string): Observable<Response> {
        // Build the url
        var url = PolicyService.baseUrl + "/Accounts/{id}";
        url = url.replace("{id}", id);

        // Make the request
        let request = this.http.delete<any>(url).pipe(share());

        // The user can pass in a single context. Convert it to a list.
        if (contexts && !Array.isArray(contexts))
            contexts = [contexts];

        // Update the contexts (if any provided)
        _.each(contexts, context => {
            this.handleNormalDeleteRequest(null, request, idOfDeletedItem, context);
        });
        return request;
    }

    public UpdateAccount(body: any, id: string): Observable<Response> {
        // Build the url
        var url = PolicyService.baseUrl + "/Accounts/{id}";
        url = url.replace("{id}", id);

        // Make the request
        let request = this.http.patch<any>(url, body).pipe(share());

        request.subscribe(() => {});

        return request;
    }

    public GetAnnouncements(): Observable<Response> {
        // Build the url
        var url = PolicyService.baseUrl + "/Announcements";

        // Make the request
        let request = this.http.get<any>(url).pipe(share());

        request.subscribe(() => {});

        return request;
    }

    public CreateAnnouncement(body: any): Observable<Response> {
        // Build the url
        var url = PolicyService.baseUrl + "/Announcements";

        // Make the request
        let request = this.http.post<any>(url, body).pipe(share());

        request.subscribe(() => {});

        return request;
    }

    public GetActiveAnnouncements(): Observable<Response> {
        // Build the url
        var url = PolicyService.baseUrl + "/Announcements/active";

        // Make the request
        let request = this.http.get<any>(url).pipe(share());

        request.subscribe(() => {});

        return request;
    }

    public GetAnnouncement(id: string): Observable<Response> {
        // Build the url
        var url = PolicyService.baseUrl + "/Announcements/{id}";
        url = url.replace("{id}", id);

        // Make the request
        let request = this.http.get<any>(url).pipe(share());

        request.subscribe(() => {});

        return request;
    }

    public UpdateAnnouncement(body: any, id: string): Observable<Response> {
        // Build the url
        var url = PolicyService.baseUrl + "/Announcements/{id}";
        url = url.replace("{id}", id);

        // Make the request
        let request = this.http.patch<any>(url, body).pipe(share());

        request.subscribe(() => {});

        return request;
    }

    public DeleteAnnouncement(idOfDeletedItem: string, contexts: DataContext[] | DataContext, id: string): Observable<Response> {
        // Build the url
        var url = PolicyService.baseUrl + "/Announcements/{id}";
        url = url.replace("{id}", id);

        // Make the request
        let request = this.http.delete<any>(url).pipe(share());

        // The user can pass in a single context. Convert it to a list.
        if (contexts && !Array.isArray(contexts))
            contexts = [contexts];

        // Update the contexts (if any provided)
        _.each(contexts, context => {
            this.handleNormalDeleteRequest(null, request, idOfDeletedItem, context);
        });
        return request;
    }

    public Citystatezipcities(): Observable<Response> {
        // Build the url
        var url = PolicyService.baseUrl + "/CityStateZip/cities";

        // Make the request
        let request = this.http.get<any>(url).pipe(share());

        request.subscribe(() => {});

        return request;
    }

    public Citystatezipstates(): Observable<Response> {
        // Build the url
        var url = PolicyService.baseUrl + "/CityStateZip/states";

        // Make the request
        let request = this.http.get<any>(url).pipe(share());

        request.subscribe(() => {});

        return request;
    }

    public Citystatezipzipcodes(): Observable<Response> {
        // Build the url
        var url = PolicyService.baseUrl + "/CityStateZip/zipcodes";

        // Make the request
        let request = this.http.get<any>(url).pipe(share());

        request.subscribe(() => {});

        return request;
    }

    public Citystatezipcounties(): Observable<Response> {
        // Build the url
        var url = PolicyService.baseUrl + "/CityStateZip/counties";

        // Make the request
        let request = this.http.get<any>(url).pipe(share());

        request.subscribe(() => {});

        return request;
    }

    public GetDocument(jobId: string): Observable<Response> {
        // Build the url
        var url = PolicyService.baseUrl + "/Documents/{jobId}";
        url = url.replace("{jobId}", jobId);

        // Make the request
        let request = this.http.get<any>(url).pipe(share());

        request.subscribe(() => {});

        return request;
    }

    public GetDocuments(): Observable<Response> {
        // Build the url
        var url = PolicyService.baseUrl + "/Documents";

        // Make the request
        let request = this.http.get<any>(url).pipe(share());

        request.subscribe(() => {});

        return request;
    }

    public DocumentsDocumentInfo(): Observable<Response> {
        // Build the url
        var url = PolicyService.baseUrl + "/Documents/DocumentInfo";

        // Make the request
        let request = this.http.get<any>(url).pipe(share());

        request.subscribe(() => {});

        return request;
    }

    public GetFavorites(): Observable<Response> {
        // Build the url
        var url = PolicyService.baseUrl + "/Favorites";

        // Make the request
        let request = this.http.get<any>(url).pipe(share());

        request.subscribe(() => {});

        return request;
    }

    public PostFavorites(body: any): Observable<Response> {
        // Build the url
        var url = PolicyService.baseUrl + "/Favorites";

        // Make the request
        let request = this.http.post<any>(url, body).pipe(share());

        request.subscribe(() => {});

        return request;
    }

    public DeleteFavorites(idOfDeletedItem: string, contexts: DataContext[] | DataContext): Observable<Response> {
        // Build the url
        var url = PolicyService.baseUrl + "/Favorites";

        // Make the request
        let request = this.http.delete<any>(url).pipe(share());

        // The user can pass in a single context. Convert it to a list.
        if (contexts && !Array.isArray(contexts))
            contexts = [contexts];

        // Update the contexts (if any provided)
        _.each(contexts, context => {
            this.handleNormalDeleteRequest(null, request, idOfDeletedItem, context);
        });
        return request;
    }

    public Forms(): Observable<Response> {
        // Build the url
        var url = PolicyService.baseUrl + "/Forms";

        // Make the request
        let request = this.http.get<any>(url).pipe(share());

        request.subscribe(() => {});

        return request;
    }

    public PolicyGenerateForms(body: any, storageId: string): Observable<Response> {
        // Build the url
        var url = PolicyService.baseUrl + "/Policies/{storageId}/actions/generateforms";
        url = url.replace("{storageId}", storageId);

        // Make the request
        let request = this.http.post<any>(url, body).pipe(share());

        request.subscribe(() => {});

        return request;
    }

    public GetPolicies(): Observable<Response> {
        // Build the url
        var url = PolicyService.baseUrl + "/Policies";

        // Make the request
        let request = this.http.get<any>(url).pipe(share());

        request.subscribe(() => {});

        return request;
    }

    public CreateQuote(body: any): Observable<Response> {
        // Build the url
        var url = PolicyService.baseUrl + "/Policies";

        // Make the request
        let request = this.http.post<any>(url, body).pipe(share());

        request.subscribe(() => {});

        return request;
    }

    public GetPolicy(id: string): Observable<Response> {
        // Build the url
        var url = PolicyService.baseUrl + "/Policies/{id}";
        url = url.replace("{id}", id);

        // Make the request
        let request = this.http.get<any>(url).pipe(share());

        request.subscribe(() => {});

        return request;
    }

    public UpdateQuoteOrPolicy(body: any, id: string): Observable<Response> {
        // Build the url
        var url = PolicyService.baseUrl + "/Policies/{id}";
        url = url.replace("{id}", id);

        // Make the request
        let request = this.http.patch<any>(url, body).pipe(share());

        request.subscribe(() => {});

        return request;
    }

    public RatePolicy(body: any, id: string): Observable<Response> {
        // Build the url
        var url = PolicyService.baseUrl + "/Policies/{id}/actions/rate";
        url = url.replace("{id}", id);

        // Make the request
        let request = this.http.post<any>(url, body).pipe(share());

        request.subscribe(() => {});

        return request;
    }

    public CommitPolicy(body: any, id: string): Observable<Response> {
        // Build the url
        var url = PolicyService.baseUrl + "/Policies/{id}/actions/commit";
        url = url.replace("{id}", id);

        // Make the request
        let request = this.http.post<any>(url, body).pipe(share());

        request.subscribe(() => {});

        return request;
    }

    public PolicyGenerateWorksheets(body: any, storageId: string): Observable<Response> {
        // Build the url
        var url = PolicyService.baseUrl + "/Policies/{storageId}/actions/generateworksheets";
        url = url.replace("{storageId}", storageId);

        // Make the request
        let request = this.http.post<any>(url, body).pipe(share());

        request.subscribe(() => {});

        return request;
    }

    public RevertPolicy(body: any, id: string): Observable<Response> {
        // Build the url
        var url = PolicyService.baseUrl + "/Policies/{id}/actions/revert";
        url = url.replace("{id}", id);

        // Make the request
        let request = this.http.post<any>(url, body).pipe(share());

        request.subscribe(() => {});

        return request;
    }

    public BeginTransaction(body: any, storageId: string): Observable<Response> {
        // Build the url
        var url = PolicyService.baseUrl + "/Policies/{storageId}/actions/begintransaction";
        url = url.replace("{storageId}", storageId);

        // Make the request
        let request = this.http.post<any>(url, body).pipe(share());

        request.subscribe(() => {});

        return request;
    }

    public ExportPolicy(body: any, storageId: string): Observable<Response> {
        // Build the url
        var url = PolicyService.baseUrl + "/Policies/{storageId}/actions/export";
        url = url.replace("{storageId}", storageId);

        // Make the request
        let request = this.http.post<any>(url, body).pipe(share());

        request.subscribe(() => {});

        return request;
    }

    public ExportedPolicyDownload(body: any, fileName: string, downloadId: string): Observable<Response> {
        // Build the url
        var url = PolicyService.baseUrl + "/ExportedPolicies/{fileName}/{downloadId}";
        url = url.replace("{fileName}", fileName);
        url = url.replace("{downloadId}", downloadId);

        // Make the request
        let request = this.http.post<any>(url, body).pipe(share());

        request.subscribe(() => {});

        return request;
    }

    public GetTransactionTypes(storageId: string): Observable<Response> {
        // Build the url
        var url = PolicyService.baseUrl + "/Policies/{storageId}/transaction-types";
        url = url.replace("{storageId}", storageId);

        // Make the request
        let request = this.http.get<any>(url).pipe(share());

        request.subscribe(() => {});

        return request;
    }

    public GetTransactionTypesById(storageId: string, transactionId: string): Observable<Response> {
        // Build the url
        var url = PolicyService.baseUrl + "/Policies/{storageId}/transaction-types/{transactionId}";
        url = url.replace("{storageId}", storageId);
        url = url.replace("{transactionId}", transactionId);

        // Make the request
        let request = this.http.get<any>(url).pipe(share());

        request.subscribe(() => {});

        return request;
    }

    public PolicyExtract(body: any, storageId: string): Observable<Response> {
        // Build the url
        var url = PolicyService.baseUrl + "/policies/{storageId}/actions/extract";
        url = url.replace("{storageId}", storageId);

        // Make the request
        let request = this.http.post<any>(url, body).pipe(share());

        request.subscribe(() => {});

        return request;
    }

    public CheckLock(storageId: string): Observable<Response> {
        // Build the url
        var url = PolicyService.baseUrl + "/PolicyLocks/{storageId}";
        url = url.replace("{storageId}", storageId);

        // Make the request
        let request = this.http.get<any>(url).pipe(share());

        request.subscribe(() => {});

        return request;
    }

    public RemoveLock(body: any, storageId: string): Observable<Response> {
        // Build the url
        var url = PolicyService.baseUrl + "/PolicyLocks/{storageId}/actions/unlock";
        url = url.replace("{storageId}", storageId);

        // Make the request
        let request = this.http.post<any>(url, body).pipe(share());

        request.subscribe(() => {});

        return request;
    }

    public SetLock(body: any, storageId: string): Observable<Response> {
        // Build the url
        var url = PolicyService.baseUrl + "/PolicyLocks/{storageId}/actions/lock";
        url = url.replace("{storageId}", storageId);

        // Make the request
        let request = this.http.post<any>(url, body).pipe(share());

        request.subscribe(() => {});

        return request;
    }

    public RecoveryInfo(storageId: string, transactionId: string): Observable<Response> {
        // Build the url
        var url = PolicyService.baseUrl + "/PolicyLocks/RecoveryInfo/{storageId}-{transactionId}";
        url = url.replace("{storageId}", storageId);
        url = url.replace("{transactionId}", transactionId);

        // Make the request
        let request = this.http.get<any>(url).pipe(share());

        request.subscribe(() => {});

        return request;
    }

    public RecoveryStatus(policyId: string): Observable<Response> {
        // Build the url
        var url = PolicyService.baseUrl + "/PolicyLocks/RecoveryStatus/{policyId}";
        url = url.replace("{policyId}", policyId);

        // Make the request
        let request = this.http.get<any>(url).pipe(share());

        request.subscribe(() => {});

        return request;
    }

    public TempSave(body: any, policyId: string): Observable<Response> {
        // Build the url
        var url = PolicyService.baseUrl + "/PolicyLocks/{policyId}/actions/tempsave";
        url = url.replace("{policyId}", policyId);

        // Make the request
        let request = this.http.post<any>(url, body).pipe(share());

        request.subscribe(() => {});

        return request;
    }

    public GetProducts(): Observable<Response> {
        // Build the url
        var url = PolicyService.baseUrl + "/Products";

        // Make the request
        let request = this.http.get<any>(url).pipe(share());

        request.subscribe(() => {});

        return request;
    }

    public GetRecents(): Observable<Response> {
        // Build the url
        var url = PolicyService.baseUrl + "/Recents";

        // Make the request
        let request = this.http.get<any>(url).pipe(share());

        request.subscribe(() => {});

        return request;
    }

    public UpdateRecents(body: any): Observable<Response> {
        // Build the url
        var url = PolicyService.baseUrl + "/Recents";

        // Make the request
        let request = this.http.post<any>(url, body).pipe(share());

        request.subscribe(() => {});

        return request;
    }

    public Legalentites(): Observable<Response> {
        // Build the url
        var url = PolicyService.baseUrl + "/ReferenceData/LegalEntities";

        // Make the request
        let request = this.http.get<any>(url).pipe(share());

        request.subscribe(() => {});

        return request;
    }

    public Ratingcompanies(): Observable<Response> {
        // Build the url
        var url = PolicyService.baseUrl + "/ReferenceData/RatingCompanies";

        // Make the request
        let request = this.http.get<any>(url).pipe(share());

        request.subscribe(() => {});

        return request;
    }

    public Policystatuses(): Observable<Response> {
        // Build the url
        var url = PolicyService.baseUrl + "/ReferenceData/PolicyStatuses";

        // Make the request
        let request = this.http.get<any>(url).pipe(share());

        request.subscribe(() => {});

        return request;
    }

    public GetTransactionDetail(storageId: string, transactionId: string): Observable<Response> {
        // Build the url
        var url = PolicyService.baseUrl + "/Transactions/{storageId}-{transactionId}";
        url = url.replace("{storageId}", storageId);
        url = url.replace("{transactionId}", transactionId);

        // Make the request
        let request = this.http.get<any>(url).pipe(share());

        request.subscribe(() => {});

        return request;
    }

    public ListTransactionsByStorageId(storageId: string): Observable<Response> {
        // Build the url
        var url = PolicyService.baseUrl + "/Transactions";

        // Add the query parameters that aren't null
        let params = new HttpParams();
        params = params.set('storageId', storageId);

        // Make the request
        let request = this.http.get<any>(url, { params }).pipe(share());

        request.subscribe(() => {});

        return request;
    }

    public TransactionGenerateForms(body: any, storageId: string, transactionId: string, groupId: string): Observable<Response> {
        // Build the url
        var url = PolicyService.baseUrl + "/Transactions/{storageId}-{transactionId}/actions/generateforms/{groupId}";
        url = url.replace("{storageId}", storageId);
        url = url.replace("{transactionId}", transactionId);
        url = url.replace("{groupId}", groupId);

        // Make the request
        let request = this.http.post<any>(url, body).pipe(share());

        request.subscribe(() => {});

        return request;
    }

    public TransactionGenerateWorksheets(body: any, storageId: string, transactionId: string): Observable<Response> {
        // Build the url
        var url = PolicyService.baseUrl + "/Transactions/{storageId}-{transactionId}/actions/generateworksheets";
        url = url.replace("{storageId}", storageId);
        url = url.replace("{transactionId}", transactionId);

        // Make the request
        let request = this.http.post<any>(url, body).pipe(share());

        request.subscribe(() => {});

        return request;
    }

    public ListTransactionsReasons(storageId: string): Observable<Response> {
        // Build the url
        var url = PolicyService.baseUrl + "/Transactions/Reasons";

        // Add the query parameters that aren't null
        let params = new HttpParams();
        params = params.set('storageId', storageId);

        // Make the request
        let request = this.http.get<any>(url, { params }).pipe(share());

        request.subscribe(() => {});

        return request;
    }

    public TransactionsExtract(body: any, storageId: string, transactionId: string): Observable<Response> {
        // Build the url
        var url = PolicyService.baseUrl + "/Transactions/{storageId}-{transactionId}/actions/extract";
        url = url.replace("{storageId}", storageId);
        url = url.replace("{transactionId}", transactionId);

        // Make the request
        let request = this.http.post<any>(url, body).pipe(share());

        request.subscribe(() => {});

        return request;
    }

    public CurrentUser(): Observable<Response> {
        // Build the url
        var url = PolicyService.baseUrl + "/current-user";

        // Make the request
        let request = this.http.get<any>(url).pipe(share());

        request.subscribe(() => {});

        return request;
    }

    public GetRawUser(applicationName: string): Observable<Response> {
        // Build the url
        var url = PolicyService.baseUrl + "/current-user/settings/{applicationName}";
        url = url.replace("{applicationName}", applicationName);

        // Make the request
        let request = this.http.get<any>(url).pipe(share());

        request.subscribe(() => {});

        return request;
    }

    public UpdateRawUser(body: any, applicationName: string): Observable<Response> {
        // Build the url
        var url = PolicyService.baseUrl + "/current-user/settings/{applicationName}";
        url = url.replace("{applicationName}", applicationName);

        // Make the request
        let request = this.http.patch<any>(url, body).pipe(share());

        request.subscribe(() => {});

        return request;
    }

    public GetUsersQuicksolverSettings(): Observable<Response> {
        // Build the url
        var url = PolicyService.baseUrl + "/current-user/qs-settings";

        // Make the request
        let request = this.http.get<any>(url).pipe(share());

        request.subscribe(() => {});

        return request;
    }

    public UpdateUsersQuicksolverSettings(body: any): Observable<Response> {
        // Build the url
        var url = PolicyService.baseUrl + "/current-user/qs-settings";

        // Make the request
        let request = this.http.patch<any>(url, body).pipe(share());

        request.subscribe(() => {});

        return request;
    }

    public SetAnnouncementToRead(body: any, announcementId: string): Observable<Response> {
        // Build the url
        var url = PolicyService.baseUrl + "/current-user/read-announcements/{announcementId}";
        url = url.replace("{announcementId}", announcementId);

        // Make the request
        let request = this.http.post<any>(url, body).pipe(share());

        request.subscribe(() => {});

        return request;
    }

    public SetAnnouncementToUnread(idOfDeletedItem: string, contexts: DataContext[] | DataContext, announcementId: string): Observable<Response> {
        // Build the url
        var url = PolicyService.baseUrl + "/current-user/read-announcements/{announcementId}";
        url = url.replace("{announcementId}", announcementId);

        // Make the request
        let request = this.http.delete<any>(url).pipe(share());

        // The user can pass in a single context. Convert it to a list.
        if (contexts && !Array.isArray(contexts))
            contexts = [contexts];

        // Update the contexts (if any provided)
        _.each(contexts, context => {
            this.handleNormalDeleteRequest(null, request, idOfDeletedItem, context);
        });
        return request;
    }

    public CheckUserPermission(component: string, productId: string, verb: string): Observable<Response> {
        // Build the url
        var url = PolicyService.baseUrl + "/current-user/relationships/permissions/{component}/{productId}/{verb}";
        url = url.replace("{component}", component);
        url = url.replace("{productId}", productId);
        url = url.replace("{verb}", verb);

        // Make the request
        let request = this.http.head<any>(url).pipe(share());

        request.subscribe(() => {});

        return request;
    }

    public GetRecoverablePolicies(): Observable<Response> {
        // Build the url
        var url = PolicyService.baseUrl + "/current-user/recoverable-policies";

        // Make the request
        let request = this.http.get<any>(url).pipe(share());

        request.subscribe(() => {});

        return request;
    }

}